import React, { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ReduxState } from '~/types/redux/share';
import { Comment, Profile, VideoReel, VideoReelProps } from '@vimily/bonjoro-ui-components-v2/VideoReel';
import { Reply, ReplyMediaStatus } from '~/types/models/reply';
import useCurrentUser from '~/react-hooks/use-current-user';
import { parseUtcDate, utcToLocalDate } from '~/utils/date';
import { computeTextColor } from '~/utils/Color';
import { createComment, createdComment, videoPlayView } from '../actions';
import { isVideoWatched } from '~/utils/video';
import { createReaction } from '../actions/Reactions';
import { Thumbnail } from '@vimily/bonjoro-ui-components-v2/VideoRecorder';
import axios from 'axios';
import { uploadVideoToS3 } from '~/App/Api/S3Upload';
import type { UploadUrlResponse } from '~/types/api';
import MessageTemplateEmbeds from '../containers/MessageTemplateEmbeds';
import Linkify from '~/App/Components/Linkify';

function getRelativeDate(date: string, timezone?: string) {
  const utc = parseUtcDate(date);
  const local = utcToLocalDate(utc, timezone);
  return local?.toRelative({
    style: 'short',
  });
}

const getThumbnailBlob = async (thumbnail: Thumbnail) => {
  const res = await axios.get<Blob>(thumbnail?.square.data, {
    responseType: 'blob',
  });

  return res.data;
};

export default function Mobile() {

  const { timezone } = useCurrentUser({
    queryOptions: {
      enabled: false,
    },
  });

  const dispatch = useDispatch();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [watched, setWatched] = useState(false);

  const {
    model: greet,
    assignee,
    profile,
    messageTemplate,
    features,
    reactionCount,
    comments : initialComments,
    videoWatched,
    ctaClicked : onCtaClick
  } = useSelector((state: ReduxState) => ({
    model: state.root.model,
    messageTemplate: state.root.messageTemplate,
    assignee: state.root.assignee,
    features: state.root.features,
    profile: state.root.profile,
    reactionCount: state.root.reactionCount,
    comments: state.comments.comments,
    videoWatched: state.tracking.videoWatched,
    ctaClicked: state.tracking.ctaClicked,
  }));


  const sender : Profile = {
    name: `${assignee?.first_name ?? ''} ${assignee?.last_name ?? ''}`,
    first_name: assignee?.first_name ?? undefined,
    last_name: assignee?.last_name ?? undefined,
    imageUrl: assignee?.avatar ?? undefined,
    tag: assignee?.bio ?? undefined,
  };

  const recepient : Profile = {
    imageUrl: profile?.image ?? undefined,
    name: profile?.name ?? undefined,
    first_name: profile?.first_name ?? undefined,
    last_name: profile?.last_name ?? undefined,
    tag: profile?.position ?? undefined
  };

  const comments : Comment[] = initialComments.map((comment) => {
    return {
      id: comment.id,
      text: comment.media?.video_url ? undefined: comment.content,
      profile: comment?.author?.is_recipient ? recepient : sender,
      timestamp: getRelativeDate(comment.created_at, timezone),
      video: comment.media?.video_url ? {
        url: comment.media.video_url,
        thumbnail: comment.media.thumb_url,
        status: [ReplyMediaStatus.COMPLETE].includes( comment.media.status)
          ? 'ready'
          : [ReplyMediaStatus.RECEIVED, ReplyMediaStatus.UPLOADED, ReplyMediaStatus.ENCODING].includes(comment.media.status)
            ? 'encoding'
            : comment.media.status === ReplyMediaStatus.ERROR
              ? 'error'
              : undefined,
      } : undefined,
    } as Comment;
  });

  const markVideoAsWatched = () => {
    dispatch(videoPlayView());
    videoWatched?.();
    setWatched(true);
  };

  const onProgress = useCallback(() => {
    if (!videoRef.current || videoRef.current?.paused) return;

    if (isVideoWatched(videoRef?.current) && !watched) {
      markVideoAsWatched();
    }
  }, [watched, videoRef]);


  const onReactionClick = () => {
    void dispatch(createReaction());
  };

  const createVideoReply = async () => {
    return dispatch(createComment('', true));
  };

  const onAddVideoComment = useCallback(async (
    blob: Blob,
    thumbnails: Thumbnail[],
  ) => {

    if (!blob || !videoRef?.current) return;

    const reply = await createVideoReply() as unknown as Reply;

    if (!reply || !reply.id) return;

    try {
      const uploadUrlResponse = await axios.get<UploadUrlResponse>(
        `/api/v2/replies/${reply.id}/media-upload-url`
      );
      const thumbnailBlob = await getThumbnailBlob(thumbnails[0]);

      const { url } = uploadUrlResponse.data;

      await uploadVideoToS3(url, blob);

      const formData = new FormData();
      if (thumbnailBlob) formData.append('thumbnail', thumbnailBlob);

      await axios.post(`/api/v2/replies/${reply.id}/media`, formData);
      reply.media = {
        video_url: videoRef.current.src,
        status: ReplyMediaStatus.COMPLETE,
      };

      dispatch(createdComment(reply));
    } catch (e) {
      console.error('Error uploading video', e);
    }
  }, [videoRef]);

  const onAddTextComment = (value: string) => {
    void dispatch(createComment(value));
  };

  const props : VideoReelProps = {
    callbacks: {
      onAddTextComment,
      onAddVideoComment,
      onReactionClick,
      onProgress,
      onCtaClick,
    },
    branding: {
      showBadge: !features?.shouldHideBonjoroBranding,
      badgeUrl: 'https://bonjoro.com',
      backgroundUrl: messageTemplate?.background_url ?? undefined,
      logoUrl: messageTemplate?.logo_url ?? undefined,
    },
    recepient,
    sender,
    media: {
      forceFirstFrame: false,
      videoUrl: greet?.video_url ? `${greet?.video_url}?t=0.1` : undefined,
      videoThumbnail: greet?.thumb_url ?? undefined,
      preload: 'auto',
      transcription: greet?.transcript?.captions?.sentences ?? [],
      initialTranscriptionState: 'open'
    },
    message: greet?.message ? <Linkify text={greet.message} /> : undefined,
    cta: messageTemplate.cta.is_displayed ? {
      text: messageTemplate.cta.text,
      url: messageTemplate.cta.trueUrl,
      containerStyle: {
        backgroundColor: messageTemplate.primary_color,
      },
      iconStyle: {
        color: computeTextColor(messageTemplate.primary_color),
      },
      textStyle: {
        color: computeTextColor(messageTemplate.primary_color),
      },
    } : undefined,
    references: {
      videoRef,
    },
    embeds: <MessageTemplateEmbeds
      messageTemplate={messageTemplate}
      embedContainerClassName='v2-min-h-[400px] v2-h-[400px]'
      embedClassName='v2-w-full v2-h-[400px] v2-bg-black v2-bg-opacity-50'
    />,
    reactionCount,
    comments,
  };

  return <VideoReel {...props} />;
}
